import { Save, Upload, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setGlobalProductPopupElement } from '../features/popupSlice';
import { CustomFooterItem } from '../types/footer';
import { getImageUrl } from '../utils';
import ImagePopUpV3 from './ImagePopUpV3';

interface SaveFooterModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (footerData: any) => Promise<CustomFooterItem>;
    footer?: CustomFooterItem; // Existing footer data for editing, if any
    isEdit?: boolean;
}

export const SaveFooterModal: React.FC<SaveFooterModalProps> = ({
    isOpen,
    onClose,
    onSave,
    footer,
    isEdit = false
}) => {
    const dispatch = useDispatch();

    // Form state
    const [name, setName] = useState<string>('');
    const [image, setImage] = useState<string>('https://placehold.co/1200x300');
    const [description, setDescription] = useState<string>('');
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Set initial form values when editing
    useEffect(() => {
        if (footer) {
            setName(footer.name);
            setImage(footer.images);
            setDescription(footer.description || '');
        } else {
            // Reset form for new footer
            setName('');
            setImage('https://placehold.co/1200x300');
            setDescription('');
        }
        // Reset errors when opening modal
        setErrors({});
    }, [footer, isOpen]);

    // Validate form
    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!name.trim()) {
            newErrors.name = 'Footer name is required';
        }

        if (!image) {
            newErrors.image = 'Footer image is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSaving(true);

        try {
            const footerData: CustomFooterItem = {
                ...(footer?._id ? { id: footer._id } : {}),
                name,
                image,
                description
            } as any;

            await onSave(footerData);
            onClose();
        } catch (error) {
            console.error('Error saving footer:', error);
            setErrors({
                submit: 'Failed to save footer. Please try again.'
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Handle image upload (mock)
    const handleUpload = () => {
        dispatch(
            setGlobalProductPopupElement(
                <ImagePopUpV3
                    onClose={() => {
                        dispatch(setGlobalProductPopupElement(null));
                    }}
                    onUpdate={(images) => {
                        setImage(images?.[0]?.path || "");
                        dispatch(setGlobalProductPopupElement(null));
                    }}
                    single
                />
            )
        );
    };

    // If modal is closed, don't render
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                {/* Modal footer */}
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-semibold">
                        {isEdit ? 'Edit Footer' : 'Create New Footer'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-full hover:bg-gray-100"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Modal body - Form */}
                <form onSubmit={handleSubmit} style={{ display: "block" }}>
                    <div className="p-4 space-y-4">
                        {/* Name field */}
                        <div>
                            <label htmlFor="footer-name" className="block text-sm font-medium text-gray-700 mb-1">
                                Footer Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="footer-name"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className={`w-full p-2 border rounded-md ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="Enter footer name"
                            />
                            {errors.name && (
                                <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                            )}
                        </div>

                        {/* Image upload */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Footer Image <span className="text-red-500">*</span>
                            </label>
                            <div className="border rounded-md overflow-hidden">
                                {/* Image preview */}
                                <button type="button"
                                    onClick={handleUpload} className="w-full h-24 bg-gray-100 relative">
                                    {image && (
                                        <img
                                            src={image ? getImageUrl(image) : "https://placehold.co/1200x300"}
                                            alt="Footer"
                                            className="object-cover w-full h-full"
                                        />
                                    )}

                                    {/* Upload overlay */}
                                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                        <label htmlFor="image-upload" className="bg-white p-2 rounded-md cursor-pointer flex items-center">
                                            <Upload className="w-4 h-4 mr-1" />
                                            <span className="text-sm">Change Image</span>
                                        </label>
                                    </div>
                                </button>
                            </div>
                            {errors.image && (
                                <p className="mt-1 text-sm text-red-500">{errors.image}</p>
                            )}
                            <p className="mt-1 text-xs text-gray-500">
                                Recommended size: 1200×300px. Max file size: 2MB.
                            </p>
                        </div>

                        {/* Description field */}
                        <div>
                            <label htmlFor="footer-description" className="block text-sm font-medium text-gray-700 mb-1">
                                Description
                            </label>
                            <textarea
                                id="footer-description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-md"
                                rows={3}
                                placeholder="Brief description of this footer"
                            />
                        </div>

                        {/* General error message */}
                        {errors.submit && (
                            <div className="p-2 bg-red-50 border border-red-200 rounded-md">
                                <p className="text-sm text-red-600">{errors.submit}</p>
                            </div>
                        )}
                    </div>

                    {/* Modal footer */}
                    <div className="p-4 border-t flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-gray-700"
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center"
                            disabled={isSaving}
                        >
                            <Save className="w-4 h-4 mr-2" />
                            {isSaving ? 'Saving...' : 'Save Footer'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Usage example component
const SaveFooterModalExample: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [currentFooter, setCurrentFooter] = useState<CustomFooterItem | undefined>(undefined);

    const handleOpenNewModal = (): void => {
        setIsEditMode(false);
        setCurrentFooter(undefined);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (): void => {
        setIsEditMode(true);
        // setCurrentFooter(exampleFooter);
        setIsModalOpen(true);
    };

    const handleSaveFooter = async (footerData: any): Promise<CustomFooterItem> => {
        // Simulate API call
        return new Promise((resolve) => {
            setTimeout(() => {
                console.log('Saved footer data:', footerData);
                resolve(footerData);
            }, 1000);
        });
    };

    return (
        <div className="p-4 space-y-4">
            <button
                onClick={handleOpenNewModal}
                className="px-4 py-2 bg-blue-600 text-white rounded mr-4"
            >
                Create New Footer
            </button>

            <button
                onClick={handleOpenEditModal}
                className="px-4 py-2 bg-green-600 text-white rounded"
            >
                Edit Existing Footer
            </button>

            <SaveFooterModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSaveFooter}
                footer={currentFooter}
                isEdit={isEditMode}
            />
        </div>
    );
};

export default SaveFooterModalExample;